<template>
  <LayoutsContainer padding-top="larger">
    <!-- Loading / Error -->
    <div v-if="pending" class="py-20 text-center">Loading articles…</div>
    <div v-else-if="error" class="py-20 text-center text-red-600">Failed to load articles</div>

    <div v-else>
      <!-- Page Hero -->
      <ArticlesHubHero :heading="hub?.heroHeading" :teaser="hub?.heroTeaser">
        <div class="relative max-w-md">
          <Icon
            name="icon:search"
            class="text-brand-500 pointer-events-none absolute top-1/2 left-4 h-5 w-5 -translate-y-1/2"
          />

          <input
            v-model="searchTerm"
            :placeholder="hub?.heroSearchPromptText || 'Search articles'"
            class="border-accent-500 focus:ring-accent-500 w-full rounded-lg border-1 bg-white py-3 pr-10 pl-12 text-black focus:ring-2 focus:outline-none"
          />

          <!-- Clear search button -->
          <button
            v-if="searchTerm"
            type="button"
            class="text-brand-500 hover:text-brand-700 absolute top-1/2 right-3 -translate-y-1/2"
            aria-label="Clear search"
            @click="clearSearch"
          >
            ✕
          </button>
        </div>
      </ArticlesHubHero>

      <BaseDivider class="max-md:mt-6 max-md:mb-10 md:my-16" />

      <!-- Filter by Topic -->
      <div class="mb-10 flex flex-col items-center max-md:gap-3.5 md:flex-row">
        <!-- All Articles pill -->
        <button
          v-if="allArticlesSelected"
          type="button"
          class="!bg-accent-100 !border-accent-500 !rounded-lg border-1 !px-5 !py-2.5 font-medium !text-black"
          @click.prevent="clearAllArticles"
        >
          All Articles
          <span class="text-brand-500 ml-2.5">✕</span>
        </button>
        <!-- Selected topic pills -->
        <div v-if="selectedTopics.length" class="flex flex-wrap gap-2">
          <button
            v-for="slug in selectedTopics"
            :key="slug"
            type="button"
            class="!bg-accent-100 !border-accent-500 !rounded-lg border-1 !px-5 !py-2.5 font-medium !text-black max-md:order-last"
            @click.prevent="
              () => {
                clearTopic(slug);
              }
            "
          >
            {{ availableTopics.find((t) => t.slug === slug)?.name }}
            <span class="text-brand-500 ml-2.5">✕</span>
          </button>
        </div>

        <!-- Topic dropdown -->
        <div ref="topicDropdownRef" class="relative md:ml-auto">
          <button
            class="border-accent-500 text-brand-900 hover:bg-brand-50 flex min-w-[230px] items-center justify-between rounded-md border bg-white px-3 py-2.5 font-medium shadow-sm transition"
            @click="isTopicDropdownOpen = !isTopicDropdownOpen"
          >
            <span>Filter by topic</span>

            <svg
              :class="['h-4 w-4 transition-transform', isTopicDropdownOpen ? 'rotate-180' : '']"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Dropdown list -->
          <ul
            v-if="isTopicDropdownOpen"
            class="border-brand-200 absolute right-0 left-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border bg-white shadow-lg"
          >
            <!-- All Articles option -->
            <li>
              <button
                class="hover:bg-brand-50 text-brand-600 flex w-full items-center justify-between px-4 py-2 text-left transition"
                @click="
                  clearAllTopics();
                  isTopicDropdownOpen = false;
                "
              >
                All Articles
                <span v-if="allArticlesSelected">✓</span>
              </button>
            </li>

            <!-- Topic options -->
            <li v-for="topic in availableTopics" :key="topic.slug">
              <button
                class="hover:bg-brand-50 text-brand-600 flex w-full items-center justify-between px-4 py-2 text-left transition"
                @click="onTopicClick(topic.slug)"
              >
                {{ topic.name }}
                <span v-if="selectedTopics.includes(topic.slug)">✓</span>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Featured articles -->
      <section v-if="viewMode === 'featured'">
        <section v-if="primaryFeaturedArticle">
          <TypographyTitle size="lg" as="h2" class="mb-8 tracking-tighter">featured <em>articles</em></TypographyTitle>
          <div class="mb-20 grid grid-cols-1 gap-10 lg:grid-cols-2">
            <!-- Primary Featured Article -->
            <article class="flex flex-col gap-6">
              <a :href="withTrailingSlash(`/articles/${primaryFeaturedArticle.slug}`)" class="group">
                <div
                  v-if="getImage(primaryFeaturedArticle)"
                  class="mx-auto aspect-square overflow-hidden rounded-2xl bg-gray-100 sm:max-w-[342px] md:max-w-[588px] lg:max-w-full"
                >
                  <NuxtImg
                    :src="`${getImage(primaryFeaturedArticle)?.sourceUrl}`"
                    :alt="primaryFeaturedArticle.title"
                    class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>

                <div class="mt-6 flex flex-col gap-3">
                  <h3 class="group-hover:text-brand-500 text-xl leading-tight font-black">
                    {{ primaryFeaturedArticle.title }}
                  </h3>

                  <p class="text-brand-700 text-[13px] font-normal">
                    <!-- {{ formatDate(primaryFeaturedArticle.date) }} -->
                    <span v-if="primaryFeaturedArticle.topics?.length">
                      <!-- <span class="text-accent-500">&bull;</span> -->
                      {{ primaryFeaturedArticle.topics[0]?.name }}
                    </span>
                  </p>

                  <div
                    v-if="primaryFeaturedArticle.excerpt"
                    class="text-brand-700 line-clamp-1 max-w-full"
                    v-html="primaryFeaturedArticle.excerpt"
                  />
                </div>
              </a>
            </article>

            <!-- Secondary Featured Articles -->
            <div class="flex flex-col gap-8">
              <article v-for="article in secondaryFeaturedArticles" :key="article.id" class="flex gap-6">
                <a :href="withTrailingSlash(`/articles/${article.slug}`)" class="group flex gap-6 max-sm:flex-col">
                  <div
                    v-if="getImage(article)"
                    class="aspect-[16/9] flex-shrink-0 overflow-hidden rounded-xl bg-gray-100 md:aspect-[4/3] md:w-[250px]"
                  >
                    <NuxtImg
                      :src="`${getImage(article)?.sourceUrl}`"
                      :alt="article.title"
                      class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </div>

                  <div class="flex flex-col gap-2">
                    <h4 class="group-hover:text-brand-500 text-xl leading-snug font-black">
                      {{ article.title }}
                    </h4>

                    <p class="text-[13px] font-normal text-gray-700">
                      <!-- {{ formatDate(article.date) }} -->

                      <span v-if="article.topics?.length">
                        <!-- <span class="text-accent-500">&bull;</span> -->
                        {{ article.topics[0]?.name }}
                      </span>
                    </p>

                    <div
                      v-if="article.excerpt"
                      class="line-clamp-3 max-w-full text-sm text-gray-700"
                      v-html="article.excerpt"
                    />
                  </div>
                </a>
              </article>
            </div>
          </div>
        </section>

        <!-- Topic Rows -->
        <section v-if="hub?.topicRows?.length">
          <div v-for="(row, idx) in hub.topicRows" :key="idx" class="mb-20">
            <div class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
              <TypographyTitle size="lg" as="h2" class="tracking-tighter lowercase" :html="row.heading" />
            </div>

            <HorizontalSlider
              button-variant="primary"
              :button-text="`View All ${row.topics[0]?.name} Articles`"
              :button-link="`/articles/category/${row.topics[0]?.slug}`"
            >
              <ArticlesHubCard
                v-for="article in row.articles.slice(0, 6)"
                :key="article.id"
                :article="article"
                class="w-75 md:w-98"
              />
              <!--75*4 = 300px, 98*4 = 392px-->
            </HorizontalSlider>
          </div>
        </section>
      </section>

      <!-- All Articles -->
      <section v-else>
        <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <ArticlesHubCard v-for="article in displayedArticles" :key="article.id" :article="article" />
        </div>
        <div v-if="canLoadMore" class="mt-12 flex justify-center">
          <BaseButton variant="primary" @click="loadMore">Load More</BaseButton>
        </div>
      </section>
    </div>
  </LayoutsContainer>
</template>

<script setup lang="ts">
import type { PostTypeSeo } from '~~/server/types/generated';
import HorizontalSlider from '../../components/HorizontalSlider.vue';
import { withTrailingSlash } from '~/utils/urls';

const PAGE_SIZE = 9;

/* ----------------------------------------------
   Types (normalized API)
---------------------------------------------- */
interface ArticleImage {
  sourceUrl: string;
  altText?: string;
  srcSet?: string;
}

interface Article {
  id: string;
  title: string;
  slug: string;
  date?: string;
  excerpt?: string;

  featuredImage?: ArticleImage | null;
  heroImage?: ArticleImage | null;

  introduction?: {
    content?: string;
  };

  topics?: {
    id: string;
    name: string;
    slug: string;
  }[];

  areasOfStudy?: { id: string; name?: string; slug?: string }[];
}

interface TopicRow {
  heading: string;
  articles: Article[];
  topics: {
    id: string;
    name: string;
    slug: string;
  }[];
  filterByTopicButton?: {
    id: string;
    name: string;
    slug: string;
  };
}

interface ArticleHub {
  heroHeading?: string;
  heroTeaser?: string;
  heroSearchPromptText?: string;
  featuredImage?: ArticleImage | null;
  featuredArticles?: Article[];
  topicRows?: TopicRow[];
  seo: PostTypeSeo;
}

interface ArticlesIndexResponse {
  hub: ArticleHub;
  articles: Article[];
}

/* ----------------------------------------------
   Fetch data
---------------------------------------------- */
const { data, pending, error } = await useFetch<ArticlesIndexResponse>('/api/articles/?orderBy=DATE&order=DESC');

const hub = computed(() => data.value!.hub);
const articles = computed(() => data.value?.articles ?? []);

useBellevueSEO(hub.value.seo);

/* ----------------------------------------------
   Featured Articles
---------------------------------------------- */
const featuredArticles = computed(() => hub.value?.featuredArticles ?? []);
const primaryFeaturedArticle = computed(() => featuredArticles.value[0] ?? null);
const secondaryFeaturedArticles = computed(() => featuredArticles.value.slice(1, 4));

/* ----------------------------------------------
   Curated + unified article pool
   - Includes API articles + relationship-picked articles
   - Curated wins on conflicts, but we merge to keep all fields
---------------------------------------------- */
const curatedArticles = computed(() => {
  const fromFeatured = featuredArticles.value ?? [];
  const fromRows = (hub.value?.topicRows ?? []).flatMap((row) => row.articles ?? []);
  return [...fromFeatured, ...fromRows];
});

const allArticlesPool = computed<Article[]>(() => {
  const map = new Map<string, Article>();

  // Seed with API
  for (const a of articles.value) {
    if (a?.id) map.set(a.id, a);
  }

  // Merge curated on top
  for (const c of curatedArticles.value) {
    if (!c?.id) continue;
    const existing = map.get(c.id);
    map.set(c.id, existing ? { ...existing, ...c } : c);
  }

  return Array.from(map.values());
});

/* ----------------------------------------------
   Search (title only)
---------------------------------------------- */
const searchTerm = ref('');

const filteredArticles = computed<Article[]>(() => {
  if (!searchTerm.value.trim()) return allArticlesPool.value;

  const term = searchTerm.value.toLowerCase();
  return allArticlesPool.value.filter((a) => a.title?.toLowerCase().includes(term));
});

const clearSearch = () => {
  searchTerm.value = '';
  visibleCount.value = PAGE_SIZE;

  if (selectedTopics.value.length) {
    viewMode.value = 'filtered';
  } else {
    viewMode.value = 'featured';
    allArticlesSelected.value = false;
  }
};

/* ----------------------------------------------
   Helpers
---------------------------------------------- */
const formatDate = (date?: string) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const fallBackImage: ArticleImage = {
  sourceUrl: '/images/article-placeholder.jpg',
  altText: 'Article image',
};

const getImage = (article: Article | null): ArticleImage => {
  if (!article) return fallBackImage;
  return article.heroImage || article.featuredImage || fallBackImage;
};

/* ----------------------------------------------
   Topic filtering state
---------------------------------------------- */
type ArticleViewMode = 'featured' | 'all' | 'filtered';

const viewMode = ref<ArticleViewMode>('featured');

const selectedTopics = ref<string[]>([]);
const visibleCount = ref(PAGE_SIZE);
const allArticlesSelected = ref(false);

const isTopicDropdownOpen = ref(false);

const topicFilteredArticles = computed<Article[]>(() => {
  if (!selectedTopics.value.length) return [];

  return allArticlesPool.value.filter((article) => article.topics?.some((t) => selectedTopics.value.includes(t.slug)));
});

const sortedFilteredArticles = computed<Article[]>(() => {
  const list = [...topicFilteredArticles.value];

  // Latest → Oldest
  list.sort((a, b) => {
    if (!a.date || !b.date) return 0;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  return list;
});

const canLoadMore = computed(() => {
  if (viewMode.value === 'filtered') {
    return visibleCount.value < sortedFilteredArticles.value.length;
  }

  if (viewMode.value === 'all') {
    return visibleCount.value < filteredArticles.value.length;
  }

  return false;
});

const loadMore = () => {
  visibleCount.value += PAGE_SIZE;
};

const toggleTopic = (slug: string) => {
  allArticlesSelected.value = false;

  if (selectedTopics.value.includes(slug)) {
    selectedTopics.value = selectedTopics.value.filter((t) => t !== slug);
  } else {
    selectedTopics.value.push(slug);
  }

  viewMode.value = 'filtered';
  visibleCount.value = PAGE_SIZE;
};

const onTopicClick = (slug: string) => {
  toggleTopic(slug);

  // close AFTER state update
  nextTick(() => {
    isTopicDropdownOpen.value = false;
  });
};

const clearTopic = async (slug: string) => {
  selectedTopics.value = selectedTopics.value.filter((t) => t !== slug);
  allArticlesSelected.value = false;

  if (selectedTopics.value.length === 0 && !searchTerm.value) {
    viewMode.value = 'featured';
  }
};

const clearAllTopics = async () => {
  selectedTopics.value = [];
  viewMode.value = 'all';
  visibleCount.value = PAGE_SIZE;
  allArticlesSelected.value = true;
};

const clearAllArticles = () => {
  selectedTopics.value = [];
  searchTerm.value = '';
  allArticlesSelected.value = false;
  visibleCount.value = PAGE_SIZE;
  viewMode.value = 'featured';
};

/* ----------------------------------------------
   Topic dropdown options (from unified pool)
---------------------------------------------- */
type TopicOption = { slug: string; name: string };

const availableTopics = computed<TopicOption[]>(() => {
  const map = new Map<string, TopicOption>();

  allArticlesPool.value.forEach((article) => {
    article.topics?.forEach((topic) => {
      if (topic.slug && topic.name) {
        map.set(topic.slug, { slug: topic.slug, name: topic.name });
      }
    });
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
});

/* ----------------------------------------------
   Displayed Articles (grid source)
---------------------------------------------- */
const displayedArticles = computed<Article[]>(() => {
  let source: Article[] = [];

  if (viewMode.value === 'filtered') {
    source = sortedFilteredArticles.value;
  } else if (viewMode.value === 'all') {
    source = filteredArticles.value; // respects search
  }

  return source.slice(0, visibleCount.value);
});

/* ----------------------------------------------
   Watchers
---------------------------------------------- */
watch(searchTerm, (value) => {
  if (value.trim()) {
    viewMode.value = 'all';
    visibleCount.value = PAGE_SIZE;
    allArticlesSelected.value = false;
  } else {
    if (selectedTopics.value.length) {
      viewMode.value = 'filtered';
    } else {
      viewMode.value = 'featured';
    }
  }
});
</script>
